import { useRef } from "react";
import ReactECharts from "echarts-for-react";
import { intToString } from "app/uicomponents/macrocomponents/PolicySummary/config";
import { getUSANumberFormat } from "app/utils/utils";

interface IncomeRow {
  planYear: number;
  year: number;
  insuredAge: number;
  income: {
    annualIncome: number;
    totalIncome: number;
  };
}

interface IncomeBarPanelProps {
  graphData: IncomeRow[];
  isDark: boolean;
  assetChartFilter: string;
}

const PRIMARY = "#004781";
const INITIAL_VISIBLE = 10;

export default function IncomeBarPanel({
  graphData,
  isDark,
  assetChartFilter,
}: IncomeBarPanelProps) {
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);
  const textColor = isDark ? "#ffffff" : "#333333";

  const hasData = graphData.some((d) => (d.income?.annualIncome ?? 0) > 0);

  if (!hasData) {
    return (
      <div className="flex justify-center items-center py-16">
        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          No income data available
        </p>
      </div>
    );
  }

  const xLabels = graphData.map((d) => {
    if (assetChartFilter === "insuredAge") return `A${d.insuredAge}`;
    if (assetChartFilter === "planYear") return `YR${d.planYear}`;
    return `${d.year}`;
  });

  const annualValues = graphData.map((d) => d.income?.annualIncome ?? 0);
  const totalValues = graphData.map((d) => d.income?.totalIncome ?? 0);
  const hasTotalIncome = totalValues.some((v) => v > 0);

  const useInitialWindow = !zoomStateRef.current;
  const initialEndIdx = Math.min(INITIAL_VISIBLE - 1, Math.max(graphData.length - 1, 0));

  const tooltipFormatter = (params: any[]) => {
    if (!params?.length) return "";
    const idx = params[0].dataIndex;
    const row = graphData[idx];

    const header = `<div style="
      background: ${isDark ? "#3A3A50" : "#E5E7EB"};
      color: ${isDark ? "#ffffff" : "#374151"};
      padding: 8px 12px;
      font-weight: bold;
      font-family: 'Conthrax Semi-Bold', Arial, sans-serif;
      border-bottom: 1px solid ${isDark ? "#4A4A60" : "#e0e0e0"};
      border-radius: 8px 8px 0 0;
      display: flex;
      align-items: center;
      gap: 10px;
    ">
      <span>YR${row.planYear}</span>
      <span style="opacity:0.4;">|</span>
      <span>${row.year}</span>
      <span style="opacity:0.4;">|</span>
      <span>A${row.insuredAge}</span>
    </div>`;

    let rows = '<div style="padding: 8px 12px;">';
    params.forEach((p) => {
      if (!p.value) return;
      rows += `<div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        margin: 2px 0;
        border-bottom: 1px solid ${isDark ? "#4A4A60" : "#E5E7EB"};
        gap: 12px;
      ">
        <div style="display:flex;align-items:center;gap:8px;">
          <div style="width:8px;height:8px;border-radius:50%;background:${
            p.color
          };flex-shrink:0;"></div>
          <span style="color:${isDark ? "#ffffff" : "#374151"};font-size:12px;">${
        p.seriesName
      }</span>
        </div>
        <span style="color:${
          isDark ? "#ffffff" : "#374151"
        };font-size:12px;font-weight:600;min-width:80px;text-align:right;">
          $${getUSANumberFormat(p.value, false, true)}
        </span>
      </div>`;
    });
    rows += "</div>";

    return `<div style="
      background:${isDark ? "#2A2A3D" : "#ffffff"};
      border:1px solid ${isDark ? "#3A3A50" : "#E5E7EB"};
      border-radius:8px;
      box-shadow:0 4px 12px rgba(0,0,0,0.15);
      overflow:hidden;
      min-width:180px;
    ">${header}${rows}</div>`;
  };

  const series: any[] = [
    {
      name: "Annual Income",
      type: "bar",
      barWidth: 24,
      itemStyle: {
        color: isDark ? "rgba(0,71,129,0.15)" : "rgba(0,71,129,0.10)",
        borderColor: PRIMARY,
        borderWidth: 2,
        borderType: "dashed",
        borderRadius: [8, 8, 0, 0],
      },
      emphasis: {
        itemStyle: {
          color: isDark ? "rgba(0,71,129,0.25)" : "rgba(0,71,129,0.18)",
        },
      },
      data: annualValues,
    },
  ];

  if (hasTotalIncome) {
    series.push({
      name: "Total Income",
      type: "bar",
      barWidth: 24,
      itemStyle: {
        color: isDark ? "rgba(0,71,129,0.07)" : "rgba(0,71,129,0.05)",
        borderColor: `${PRIMARY}77`,
        borderWidth: 2,
        borderType: "dashed",
        borderRadius: [8, 8, 0, 0],
      },
      emphasis: {
        itemStyle: {
          color: isDark ? "rgba(0,71,129,0.14)" : "rgba(0,71,129,0.10)",
        },
      },
      data: totalValues,
    });
  }

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "none" },
      backgroundColor: "transparent",
      borderColor: "transparent",
      padding: 0,
      formatter: tooltipFormatter,
      extraCssText: "box-shadow:none;border:none!important;",
    },
    legend: { show: false },
    grid: {
      left: "1%",
      right: "2%",
      bottom: "16%",
      top: "6%",
      containLabel: true,
    },
    dataZoom: [
      {
        type: "slider",
        show: true,
        start: useInitialWindow ? undefined : zoomStateRef.current?.start ?? 0,
        end: useInitialWindow
          ? undefined
          : zoomStateRef.current?.end ??
            Math.min(100, (INITIAL_VISIBLE / (graphData.length || 1)) * 100),
        startValue: useInitialWindow ? 0 : undefined,
        endValue: useInitialWindow ? initialEndIdx : undefined,
        minValueSpan: 1,
        height: 18,
        bottom: 8,
        borderColor: "transparent",
        fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
        handleStyle: {
          color: isDark ? "#ffffff" : "#333333",
          borderColor: isDark ? "#4A4A60" : "#E5E7EB",
        },
        textStyle: { color: textColor, fontSize: 10 },
        showDetail: true,
        showDataShadow: false,
        realtime: true,
        filterMode: "filter",
        xAxisIndex: 0,
      },
    ],
    xAxis: {
      type: "category",
      data: xLabels,
      axisLabel: {
        color: textColor,
        fontFamily: "Conthrax Semi-Bold",
        fontSize: 10,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      boundaryGap: true,
    },
    yAxis: {
      type: "value",
      splitLine: { show: false },
      min: 0,
      max: (value: any) => Math.round(value.max * 1.25),
      axisLabel: {
        color: textColor,
        formatter: (v: number) => `$${intToString(v)}`,
        fontFamily: "Conthrax Semi-Bold",
        fontSize: 10,
      },
      axisLine: { show: false },
    },
    series,
  };

  return (
    <div style={{ height: 340 }}>
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "100%" }}
        onEvents={{
          dataZoom: (params: any) => {
            zoomStateRef.current = { start: params.start, end: params.end };
          },
        }}
        notMerge
      />
    </div>
  );
}
